import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileText, Calendar, ChevronRight, ShieldCheck, Search as SearchIcon, PlayCircle } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SupportCTA from '../components/common/SupportCTA';
import HelpBrowseShell, { matchesSearch } from '../components/common/HelpBrowseShell';
import { usePreferences } from '../context/PreferencesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /help/guides — grade-aware exam prep articles. Same shell as Docs
 * (search + category grid), but articles render as clickable cards
 * (each has its own /help/guides/:slug page — shareable, bookmarkable).
 *
 * Grade filter:
 *   - Logged-in user with a `preferences.grade` set:
 *       show articles targeting their grade + articles with no grade
 *       (`grades == []` means "all grades"). A chip lets them toggle
 *       "Show all grades" to see the full library.
 *   - Guest / no grade set: show everything (same as Show all).
 */
export const GuidelinesPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAllGrades, setShowAllGrades] = useState(false);
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const userGrade = preferences?.grade || null;

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [aRes, cRes] = await Promise.all([
        axios.get(`${API}/pages/guidelines`),
        axios.get(`${API}/pages/guides-categories`),
      ]);
      setArticles(aRes.data || []);
      setCategories(cRes.data || []);
    } catch (e) {
      console.error('Error loading guides:', e);
    } finally {
      setLoading(false);
    }
  };

  // Grade filter — bypassed when user toggles "Show all" or has no grade.
  const gradeFilteredArticles = useMemo(() => {
    if (!userGrade || showAllGrades) return articles;
    return articles.filter(a => {
      const grades = Array.isArray(a.grades) ? a.grades : [];
      return grades.length === 0 || grades.includes(userGrade);
    });
  }, [articles, userGrade, showAllGrades]);

  // Then search on top of the grade-filter.
  const visibleArticles = useMemo(
    () => gradeFilteredArticles.filter(a => matchesSearch(a, search)),
    [gradeFilteredArticles, search]
  );

  // Category cards use grade-filtered counts (so a user on MEO Class 2
  // sees "Preparation · 3 articles" reflecting what they'll actually see).
  const categoriesWithCount = useMemo(() => {
    const counts = new Map();
    gradeFilteredArticles.forEach(a => {
      const key = a.category_id || null;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return categories.map(c => ({ ...c, article_count: counts.get(c.id) || 0 }));
  }, [gradeFilteredArticles, categories]);

  const uncategorisedCount = useMemo(
    () => gradeFilteredArticles.filter(a => !a.category_id).length,
    [gradeFilteredArticles]
  );

  const grouped = useMemo(() => {
    const byCat = new Map();
    visibleArticles.forEach(a => {
      const key = a.category_id || '__uncat__';
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key).push(a);
    });
    const catList = categories
      .filter(c => byCat.has(c.id))
      .map(c => ({ ...c, articles: byCat.get(c.id) }));
    const uncat = byCat.get('__uncat__') || [];
    return { catList, uncat };
  }, [visibleArticles, categories]);

  const scrollToCategory = (categoryId) => {
    const el = document.getElementById(categoryId ? `cat-${categoryId}` : 'cat-uncategorised');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  const totalHits = visibleArticles.length;
  const hasContent = totalHits > 0;

  // Grade chip — only shown when user has a grade set AND some article
  // in the library targets specific grades (otherwise the filter is
  // meaningless).
  const anyGradeTargeted = articles.some(a => Array.isArray(a.grades) && a.grades.length > 0);
  const showGradeChip = userGrade && anyGradeTargeted;

  const gradeChip = showGradeChip ? (
    <button
      type="button"
      onClick={() => setShowAllGrades(v => !v)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        showAllGrades
          ? 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300'
      }`}
      data-testid="guides-grade-chip"
    >
      {showAllGrades ? (
        <>Showing all grades · <span className="underline">Filter to {userGrade}</span></>
      ) : (
        <>Showing <strong>{userGrade}</strong> · <span className="underline">Show all grades</span></>
      )}
    </button>
  ) : null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link to="/help">
          <Button variant="ghost" className="mb-4" data-testid="guides-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Help
          </Button>
        </Link>

        <HelpBrowseShell
          title="Guides"
          subtitle="Exam prep tips and best practices from our team."
          categories={categoriesWithCount}
          uncategorisedCount={uncategorisedCount}
          search={search}
          onSearchChange={setSearch}
          resultsCount={search ? totalHits : null}
          onCategoryClick={scrollToCategory}
          headerRight={gradeChip}
        />

        {hasContent ? (
          <div className="space-y-8" data-testid="guides-article-list">
            {grouped.catList.map(cat => (
              <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-700" />
                  {cat.name}
                  <span className="text-xs font-normal text-slate-400">· {cat.articles.length}</span>
                </h2>
                <div className="space-y-3">
                  {cat.articles.map(a => (
                    <GuideArticleCard key={a.id} article={a} userGrade={userGrade} onClick={() => navigate(`/help/guides/${a.slug}`)} />
                  ))}
                </div>
              </section>
            ))}
            {grouped.uncat.length > 0 && (
              <section id="cat-uncategorised" className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-slate-400" />
                  Other
                  <span className="text-xs font-normal text-slate-400">· {grouped.uncat.length}</span>
                </h2>
                <div className="space-y-3">
                  {grouped.uncat.map(a => (
                    <GuideArticleCard key={a.id} article={a} userGrade={userGrade} onClick={() => navigate(`/help/guides/${a.slug}`)} />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <EmptyGuidesState
            search={search}
            userGrade={userGrade}
            showAllGrades={showAllGrades}
            onClearSearch={() => setSearch('')}
            onShowAllGrades={() => setShowAllGrades(true)}
          />
        )}

        {/* Lightweight guides support strip — mentor framing, no ticket. */}
        <GuidesSupportCTA />
      </div>
    </Layout>
  );
};

const GuideArticleCard = ({ article, userGrade, onClick }) => {
  const grades = Array.isArray(article.grades) ? article.grades : [];
  const videoCount = Array.isArray(article.video_embeds) ? article.video_embeds.length : 0;
  const isAllGrades = grades.length === 0;
  const matchesUserGrade = userGrade && grades.includes(userGrade);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-4 border rounded-lg bg-white hover:border-emerald-600 hover:shadow-sm transition group"
      data-testid={`guide-card-${article.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">
            {article.title}
          </h3>
          {article.summary && (
            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{article.summary}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {isAllGrades ? (
              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                All grades
              </Badge>
            ) : matchesUserGrade ? (
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                For your grade
              </Badge>
            ) : (
              grades.slice(0, 3).map(g => (
                <Badge key={g} variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                  {g}
                </Badge>
              ))
            )}
            {videoCount > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1"
                data-testid={`guide-card-video-badge-${article.id}`}
              >
                <PlayCircle className="w-3 h-3" />
                {videoCount === 1 ? 'Video' : `${videoCount} videos`}
              </Badge>
            )}
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
              <Calendar className="w-3 h-3" />
              {new Date(article.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all mt-1" />
      </div>
    </button>
  );
};

const EmptyGuidesState = ({ search, userGrade, showAllGrades, onClearSearch, onShowAllGrades }) => {
  // Distinct copy based on WHY the list is empty — makes each empty
  // state actionable rather than generic.
  if (search) {
    return (
      <div className="text-center py-12 border rounded-lg bg-slate-50/50" data-testid="guides-empty-search">
        <SearchIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">No results for &ldquo;{search}&rdquo;</p>
        <p className="text-sm text-slate-500 mt-1">Try a different search — or email us for personalised guidance.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearSearch} data-testid="guides-empty-clear-search">
          Clear search
        </Button>
      </div>
    );
  }
  if (userGrade && !showAllGrades) {
    return (
      <div className="text-center py-12 border rounded-lg bg-slate-50/50" data-testid="guides-empty-grade">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">No guides for {userGrade} yet.</p>
        <p className="text-sm text-slate-500 mt-1">Guides for other grades may still be useful — take a look.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onShowAllGrades} data-testid="guides-empty-show-all">
          Show all grades
        </Button>
      </div>
    );
  }
  return (
    <div className="text-center py-12 border rounded-lg bg-slate-50/50" data-testid="guides-empty-none">
      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-700 font-medium">Guides are being prepared.</p>
      <p className="text-sm text-slate-500 mt-1">Please check back later.</p>
    </div>
  );
};

/**
 * Guides-specific support strip — email only, mentor framing.
 * Reuses the public support-contacts endpoint (via SupportCTA fetch
 * would be simplest, but we render distinct copy so we inline it).
 */
const GuidesSupportCTA = () => {
  const [email, setEmail] = useState('');
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/email/public/support-contacts`)
      .then(r => { if (alive) setEmail((r.data?.support_email || '').trim()); })
      .catch(() => { });
    return () => { alive = false; };
  }, []);
  if (!email) return null;
  return (
    <div
      className="mt-10 rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50/60 to-white p-5"
      data-testid="guides-support-cta"
    >
      <p className="font-semibold text-slate-900">Need personalised guidance?</p>
      <p className="text-sm text-slate-600 mt-1">
        Have a question about your prep? Drop us a note — we&apos;ll get back to you.
      </p>
      <a
        href={`mailto:${email}?subject=Exam%20prep%20guidance`}
        className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition"
        data-testid="guides-support-email-link"
      >
        Email us at {email}
      </a>
    </div>
  );
};

/**
 * Single Guideline Article Page — unchanged content flow, but Back link
 * now points at the new canonical `/help/guides` and the top-of-page
 * HelpTabsBar has been removed (Help hub is the new nav anchor).
 */
export const GuidelineArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/pages/guidelines/${slug}`);
      setArticle(response.data);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <Link to="/help/guides">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Guides
            </Button>
          </Link>
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Article not found</p>
                <p className="text-sm mt-2">The article you&apos;re looking for doesn&apos;t exist or has been removed.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link to="/help/guides">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Guides
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{article.title}</CardTitle>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Published: {new Date(article.created_at).toLocaleDateString()}
              </div>
              {article.updated_at !== article.created_at && (
                <div className="flex items-center gap-1">
                  Updated: {new Date(article.updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div
              className="legal-page-content"
              dangerouslySetInnerHTML={{ __html: article.content.replace(/&nbsp;/g, ' ') }}
            />
            {/* Video embeds — YouTube or Dadan, URLs already normalised
                to canonical /embed/... by the backend at write time so
                we can iframe them directly. Responsive 16:9 wrapper
                keeps them clean on mobile. */}
            {Array.isArray(article.video_embeds) && article.video_embeds.length > 0 && (
              <div className="mt-6 space-y-4" data-testid={`guideline-article-videos-${article.id}`}>
                {article.video_embeds.map((v, i) => (
                  <div key={v.id || i} className="space-y-1">
                    {v.title && (
                      <p className="text-sm font-medium text-slate-700">{v.title}</p>
                    )}
                    <div
                      className="relative rounded-lg overflow-hidden bg-black"
                      style={{ paddingBottom: '56.25%' }}
                    >
                      <iframe
                        src={v.url}
                        title={v.title || `Guideline video ${i + 1}`}
                        className="absolute inset-0 w-full h-full border-0"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allow="fullscreen; autoplay; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <SupportCTA variant="article" />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default GuidelinesPage;
